// src/composables/usePermissions.ts
import { computed } from 'vue';
import { useAuthStore } from '@/stores/authStore';

export function usePermissions() {
  const authStore = useAuthStore();

  /**
   * Obtiene todos los permisos del usuario actual
   */
  const userPermissions = computed(() => {
    if (!authStore.user) return [];

    const permissions: string[] = [];
    
    // Recorrer roles del usuario
    authStore.user.authUserRoles?.forEach((userRole) => {
      // Recorrer permisos de cada rol
      userRole.authRoles?.authRolePermissions?.forEach((rolePermission) => {
        if (rolePermission.state === 1 && rolePermission.authPermissions) {
          permissions.push(rolePermission.authPermissions.name);
        }
      });
    });

    return permissions;
  });

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param permission - Nombre del permiso (ej: 'baret_navbar:dashboard')
   */
  const hasPermission = (permission: string): boolean => {
    return userPermissions.value.includes(permission);
  };

  /**
   * Verifica si el usuario tiene al menos uno de los permisos dados
   * @param permissions - Array de nombres de permisos
   */
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Verifica si el usuario tiene todos los permisos dados
   * @param permissions - Array de nombres de permisos
   */
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Obtiene el rol actual del usuario (el primero si tiene múltiples)
   */
  const userRole = computed(() => {
    return authStore.user?.authUserRoles?.[0]?.authRoles?.name || null;
  });

  /**
   * Verifica si el usuario tiene un rol específico
   * @param roleName - Nombre del rol
   */
  const hasRole = (roleName: string): boolean => {
    return authStore.user?.authUserRoles?.some(
      (userRole) => userRole.authRoles?.name === roleName
    ) || false;
  };

  return {
    userPermissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userRole,
    hasRole,
  };
}